/**
 * Runtime configuration, read once at module load.
 *
 * Everything here is public by definition — it ships in the bundle. The Firebase
 * web config is *supposed* to be public (it identifies the project, it does not
 * authorise anything), but that makes it worth being explicit: no secret ever
 * belongs in a `VITE_` variable, because Vite inlines them into the JavaScript
 * every visitor downloads.
 */

type RawEnv = Record<string, string | undefined>

const raw = import.meta.env as unknown as RawEnv

function required(key: string): string {
  const value = raw[key]?.trim()
  if (!value || value.startsWith('REPLACE')) {
    // Failing loudly at boot beats a blank sign-in page and a console error that
    // only says "auth/invalid-api-key".
    throw new Error(
      `${key} is not configured. Copy .env.example to .env.local and fill in the Firebase web config.`,
    )
  }
  return value
}

function optional(key: string, fallback = ''): string {
  return raw[key]?.trim() || fallback
}

export const env = {
  /**
   * Empty by default, which makes API calls relative (`/v1/...`). That works both
   * behind the Vite dev proxy and behind a Cloud Run load balancer serving the
   * portal and the API on one origin — no CORS, no preflight. Set it only when the
   * API is genuinely on another host.
   */
  apiBaseUrl: optional('VITE_API_BASE_URL').replace(/\/$/, ''),

  /**
   * When set (e.g. `127.0.0.1:9099`), the web SDK talks to the local Firebase
   * Auth emulator instead of the real service. Only the E2E harness sets it;
   * it is never defined in a deployed environment, so the branch it guards is
   * dead code in production bundles.
   */
  firebaseAuthEmulatorHost: optional('VITE_FIREBASE_AUTH_EMULATOR_HOST'),

  firebase: {
    apiKey: required('VITE_FIREBASE_API_KEY'),
    authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: required('VITE_FIREBASE_PROJECT_ID'),
    appId: required('VITE_FIREBASE_APP_ID'),
    // Not needed for auth-only usage; included so the same config works if
    // Firestore or Storage is ever added.
    storageBucket: optional('VITE_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: optional('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  },

  /**
   * The host the release pipeline writes into every manifest, so the two verification
   * links on the Downloads page reach the same bytes the installer does:
   * `cli-release.yml` publishes with
   * `R2_PUBLIC_BASE_URL=https://downloads.airsemantics.dev`.
   *
   * It stays on the old domain deliberately. That name is the CNAME R2 serves today;
   * `downloads.archis.dev` needs a custom-domain binding in the account that owns the
   * bucket and does not resolve yet, and a link to a host that does not exist is the
   * same dead end this default replaced when it named the GCS bucket #64 destroyed.
   * The two move together — this and `R2_PUBLIC_BASE_URL` — or the page and the
   * installer disagree about where the bytes are.
   */
  downloadsBaseUrl: optional(
    'VITE_DOWNLOADS_BASE_URL',
    'https://downloads.airsemantics.dev',
  ),
  supportEmail: optional('VITE_SUPPORT_EMAIL', 'support@airsemantics.dev'),

  /**
   * Normally empty, and normally should be: the documentation is part of this app at
   * `/docs`, so the default is a client-side route with no configuration at all.
   *
   * Setting this overrides that with an external URL, for the day documentation moves
   * to its own host. It takes precedence wherever a docs link is rendered.
   */
  docsUrl: optional('VITE_DOCS_URL'),
  /**
   * Optional Cloudflare Turnstile Site Key for invisible bot protection on public forms.
   * If not set, Turnstile verification is skipped gracefully.
   */
  turnstileSiteKey: optional('VITE_CLOUDFLARE_TURNSTILE_SITE_KEY'),

  /**
   * Optional PostHog product analytics. Empty means every capture/identify is a
   * no-op — local development and prerender stay quiet without a project.
   *
   * The token is a *project* token (public by design for browser SDKs), not a
   * personal API key. Never put a personal API key in a VITE_ variable.
   *
   * Prefer VITE_POSTHOG_PROJECT_TOKEN (PostHog's current name). VITE_POSTHOG_KEY
   * remains as a fallback for older env files.
   */
  posthog: {
    key: optional('VITE_POSTHOG_PROJECT_TOKEN') || optional('VITE_POSTHOG_KEY'),
    host: optional('VITE_POSTHOG_HOST', 'https://eu.i.posthog.com'),
  },
} as const

export const isProduction = raw['MODE'] === 'production' || import.meta.env.PROD
